import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

const initialState = {
    scenes: {} as {
        [parentSceneId: string]: {
            id: string;
            name: string;
            status: string;
            progress: string;
            scanProgress: string;
            lastSync: string;
            size: number;
            error?: string;
            // True only when the "error" status came from a user-initiated full
            // sync (Download button), so the mobile Settings error banner isn't
            // shown for background incremental-sync failures.
            errorFromFullSync?: boolean;
            viewerScenes: {
                id: string;
                name: string;
                lastSynced: string;
            }[];
        };
    },
    action: undefined as
        | undefined
        | {
              id?: string;
              action: "delete" | "estimate" | "fullSync" | "incrementalSync" | "pause" | "readSize";
          },
    sizeWarning: undefined as
        | undefined
        | {
              totalSize: number;
              usedSize: number;
              availableSize: number;
          },
    incrementalStorageSize: null as number | null,
    incrementalStorageLimit: null as number | null,
};

type State = typeof initialState;

export const offlineSlice = createSlice({
    name: "offline",
    initialState: initialState,
    reducers: {
        setAction: (state, action: PayloadAction<State["action"]>) => {
            state.action = action.payload;
        },
        addScene: (state, { payload: scene }: PayloadAction<State["scenes"][string]>) => {
            state.scenes[scene.id] = scene;
        },
        updateScene: (
            state,
            { payload: { id, updates } }: PayloadAction<{ id: string; updates: Partial<State["scenes"][string]> }>,
        ) => {
            const scene = state.scenes[id];
            if (scene) {
                state.scenes[id] = {
                    ...scene,
                    ...updates,
                };
            }
        },
        removeScene: (state, action: PayloadAction<string>) => {
            delete state.scenes[action.payload];
        },
        setSizeWarning: (state, action: PayloadAction<State["sizeWarning"]>) => {
            state.sizeWarning = action.payload;
        },
        setIncrementalStorageSize: (state, action: PayloadAction<number>) => {
            state.incrementalStorageSize = action.payload;
        },
        setIncrementalStorageLimit: (state, action: PayloadAction<number>) => {
            state.incrementalStorageLimit = action.payload;
        },
    },
});

export const selectOfflineAction = (state: RootState) => state.offline.action;
export const selectOfflineScenes = (state: RootState) => state.offline.scenes;
export const selectSizeWarning = (state: RootState) => state.offline.sizeWarning;
export const selectIncrementalStorageSize = (state: RootState) => state.offline.incrementalStorageSize;
export const selectIncrementalStorageLimit = (state: RootState) => state.offline.incrementalStorageLimit;

const { actions, reducer } = offlineSlice;
export { actions as offlineActions, reducer as offlineReducer };
